import { datadogRum } from "@datadog/browser-rum";
import { IdTokenResult } from "@firebase/auth-types";
import {
  KikErrorCodes,
  KikErrorMessages,
  KikErrorResponse,
  KikGenericResponse,
  KikGenericResponseBase,
  KikHttpStatusCodes,
  KikMarketingCloudDoubleOptInProcedures,
  KikResponseMessageTypes,
  KikRestVerbs,
  KikSocialSignInKeys,
  KikSocialSignInUxModes,
} from "@kikocosmeticsorg/uc-api-nest-common-fe";
import { kikUnsubscribeNewsletterParamsSchema } from "@kikocosmeticsorg/uc-api-nest-common-fe/zod";
import {
  KikCustomerLoginFields,
  KikCustomerPasswordUpdateFields,
  KikCustomerSignUpWithActionCodeFields,
  KikEmailUpdateWithActionCodeFields,
  KikPasswordResetWithActionCodeFields,
  KikProspectSendEmailVerificationFields,
  KikProspectSignUpWithActionCodeFields,
  KikSocialCustomerFields,
  KikUnsubscribeNewsletterParams,
  KikUserZodSchemas,
} from "@kikocosmeticsorg/uc-api-nest-common-fe/zod";
import { EvaEndpoint } from "@springtree/eva-sdk-core-service";
import { SvcGetAvailableOpenIDConfigurations } from "@springtree/eva-services-authentication-openid";
import {
  AddressesAddressDto,
  CreateCustomer,
  DataModelsUserAccountType,
  IEvaServiceDefinition,
  Login,
  LoginResponse,
  OrdersDtoOrderDto,
  SvcCreateCustomer,
  SvcGetCurrentUser,
  SvcLogin,
  SvcLogout,
  SvcUpdateUser,
  UpdateUser,
  UsersLoggedInUserDto,
  UsersSubscriptionsUserSubscriptionDto,
} from "@springtree/eva-services-core";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useGlobalNotification } from "~/hooks/use-global-notification";
import { useGuestCartStorage } from "~/hooks/use-guest-cart-storage";
import Eva from "~/services/eva";
import Firebase from "~/services/firebase";
import { HttpConfig, HttpService } from "~/services/http";
import Logger from "~/services/logger/logger";
import { StorageFactory } from "~/services/storage.factory";
import { ApiConstants } from "~/shared/api/api-constants.class";
import { ApiUtils } from "~/shared/api/api-utils.class";
import { SendEmailVerificationRequest } from "~/shared/api/auth/send-email-verification/send-email-verification.model";
import { ErrorResponse } from "~/shared/api/error-response.class";
import { CacheKeys } from "~/shared/cache-keys";
import { LoyaltyReward } from "~/shared/loyalty/loyalty-rewards.types";
import { GuestTrackOrderAuthParams } from "~/shared/user/guest-track-order-auth-params.interface";
import { LoginSignUpOrigins } from "~/shared/user/login-sign-up-origins.enum";
import AppRoutes from "~/utils/app-routes";
import invariant from "~/utils/invariant";
import TrackingUtils from "~/utils/tracking-utils";

type AuthCallConfigBody =
  | { data: KikEmailUpdateWithActionCodeFields }
  | { data: SendEmailVerificationRequest }
  | KikPasswordResetWithActionCodeFields
  | KikProspectSendEmailVerificationFields
  | KikProspectSignUpWithActionCodeFields
  | KikUnsubscribeNewsletterParams
  | GuestTrackOrderAuthParams;

type AuthCallConfig = Partial<Pick<HttpConfig<AuthCallConfigBody>, "body" | "method" | "queryParams" | "headers">> & {
  addAuth?: boolean; // Adds the firebase token as bearer
};

interface EvaHookHttpError extends Error {
  response: Response;
}

const useAuth = (initialStates?: { isLoading?: boolean }) => {
  const guestCache = useGuestCartStorage();
  const sessionStorageFactory = useMemo(() => new StorageFactory<CacheKeys>("sessionStorage"), []);
  const localStorageFactory = useMemo(() => new StorageFactory<CacheKeys>("localStorage"), []);
  const [isRememberMeDisabled, setIsRememberMeDisabled] = useState<boolean>(
    localStorageFactory.get(CacheKeys.REMEMBERME_DISABLED) === "true"
  );
  const evaTokenStorage = useMemo(
    () => (isRememberMeDisabled ? sessionStorageFactory : localStorageFactory),
    [isRememberMeDisabled, localStorageFactory, sessionStorageFactory]
  );

  const [isLoadingUser, setIsLoadingUser] = useState<boolean>(!0);
  const [isLoading, setIsLoading] = useState(!!initialStates?.isLoading);
  const [firebaseToken, setFirebaseToken] = useState<string | void>();
  const [firebaseIdTokenResult, setFirebaseIdTokenResult] = useState<IdTokenResult | void>();
  const [authError, setAuthError] = useState<ErrorResponse | null>(null);
  const [providerId, setProviderId] = useState<string>("-1");
  const [guestTrackingToken, setGuestTrackingTokenState] = useState<string | void>(() => {
    const stored = sessionStorageFactory.get(CacheKeys.GUEST_TRACKING_TOKEN);
    return typeof stored === "string" ? stored : void 0;
  });

  const unsubscribeAuthObs = useRef<() => void>();
  const tokenRefreshInProgress = useRef<Promise<string | undefined> | null>(null);

  const [currentUserToken, setCurrentUserToken] = useState<string | void>(() => {
    const stored = evaTokenStorage.get(CacheKeys.EVA_TOKEN);
    return typeof stored === "string" ? stored : void 0;
  });
  const [socialProvider, setSocialProvider] = useState<KikSocialSignInKeys | null>();
  const { data: currentUserResponse, refetch: refreshUser } = useQuery({
    queryKey: ["currentUser", currentUserToken],
    queryFn: () => {
      return callEvaService(SvcGetCurrentUser, void 0, { authenticationToken: currentUserToken! });
    },
    enabled: !!currentUserToken,
  });
  const user = currentUserResponse?.User;
  const { newErrorGlobalNotification, newWarningGlobalNotification } = useGlobalNotification();
  const router = useRouter();

  const _onLoginEvaError = (error: Error) => {
    throw error;
  };

  // EVA HOOKS - START
  const evaEndpoint = useMemo(() => new EvaEndpoint(process.env.NEXT_PUBLIC_EVA_BACKEND_URL!), []);
  const callEvaService = useCallback(
    async <T extends IEvaServiceDefinition>(
      serviceDefinition: new () => T,
      payload?: T["request"],
      options?: {
        authenticationToken?: string;
      }
    ): Promise<T["response"]> => {
      return evaEndpoint.callService(serviceDefinition, payload, {
        authenticationToken: currentUserToken || void 0,
        ...options,
      });
    },
    [evaEndpoint, currentUserToken]
  );

  const evaServiceCreateCustomer = (payload: CreateCustomer) => {
    return callEvaService(SvcCreateCustomer, payload, { authenticationToken: "" });
  };
  const evaServiceLogin = (payload: Login) => {
    return callEvaService(SvcLogin, payload, { authenticationToken: "" }).catch(_onLoginEvaError);
  };
  const evaServiceLogout = () => callEvaService(SvcLogout);
  const evaServiceOpenIdConfigs = () =>
    callEvaService(SvcGetAvailableOpenIDConfigurations, void 0, { authenticationToken: "" });
  const refreshUserData = () => refreshUser();

  const setGuestTrackingToken = (newValue?: string) => {
    setGuestTrackingTokenState(newValue);
    newValue
      ? sessionStorageFactory.set(CacheKeys.GUEST_TRACKING_TOKEN, newValue)
      : sessionStorageFactory.remove(CacheKeys.GUEST_TRACKING_TOKEN);
  };
  const updateUser = (payload: UpdateUser) => {
    return callEvaService(SvcUpdateUser, payload);
  };
  // EVA HOOKS - END

  // PRIVATE

  const _checkRedirectResult = () => {
    Logger.instance.debug("use-auth: checkRedirectResult requested");
    setIsLoading(!0);
    Firebase.instance
      .getRedirectResult()
      .finally(() => setIsLoading(!1))
      .then(function (result) {
        if (result && result.providerId) {
          const provider = Firebase.instance.providersRelation[result.providerId];
          _checkSocialUser(provider);
        }
      })
      .catch((error) => {
        Logger.instance.error(`use-auth: getRedirectResult errored`, error);
        newErrorGlobalNotification?.(
          {
            title: "generic.error",
            description: ApiUtils.errorFactory(error).messages![0].message || KikErrorMessages.GENERIC_ERROR,
          },
          { timeout: 7500 }
        );
      });
  };

  const _checkSocialUser = async (socialKey: KikSocialSignInKeys) => {
    setIsLoading(!0);
    const firebaseUser = Firebase.instance.loggedUser;
    Logger.instance.info("use-auth: social login success", firebaseUser);
    const t = await firebaseUser?.getIdToken();
    // Update the firebaseToken state
    setFirebaseToken(t);
    try {
      await _doCall(ApiConstants.endpointsMapiV1.authCustomerSocial, {
        headers: {
          Authorization: `Bearer ${t}`,
        },
      });
      // IF WE GOT A USER AND IT'S A CUSTOMER ON EVA, BASICALLY WE CAN LOG IN, OTHERWISE IT COULD BE A REGISTRATION/CONVERSION
      setSocialProvider(socialKey);
    } catch (err: any) {
      // 404 means I can register, not a real error, so I need to clear up the authError
      if (err?.status === KikHttpStatusCodes.NotFound) {
        setAuthError(null);
        const redirectUrl = new URL([`/${router.locale}`, AppRoutes.SIGN_UP].join(""), location.href);
        redirectUrl.searchParams.set("social", socialKey);

        router.push(redirectUrl.href, redirectUrl.pathname);
      }
    }
  };

  const _doCall = async <T = void>(url: string, config: AuthCallConfig): Promise<T> => {
    const { body, queryParams = {}, method, addAuth, headers = {} } = config;
    setIsLoading(!0);
    setAuthError(null);
    try {
      return await HttpService.$http<T>({
        url,
        method: method || (body ? KikRestVerbs.POST : KikRestVerbs.GET),
        body,
        queryParams,
        headers: {
          ...(addAuth ? getFirebaseTokenAuthHeader() : {}),
          ...headers,
        },
      });
    } catch (e: any) {
      if (addAuth && e?.status === KikHttpStatusCodes.Unauthorized) {
        Logger.instance.warn("use-auth: call unauthorized, refreshing firebase token and retrying", {
          endpoint: url,
        });
        try {
          return await _doCallRetry<T>(url, config);
        } catch (retryError: any) {
          Logger.instance.error("use-auth: call errored after token refresh retry", {
            endpoint: url,
            error: retryError,
          });
          setAuthError(retryError.body || new ErrorResponse());
          return Promise.reject(retryError);
        }
      }

      Logger.instance.error("use-auth: call errored", {
        endpoint: url,
        error: e,
      });
      setAuthError(e.body || new ErrorResponse());
      // We rethrow to avoid having if-else outside
      return Promise.reject(e);
    } finally {
      setIsLoading(!1);
    }
  };

  const _doCallRetry = async <T = void>(url: string, config: AuthCallConfig): Promise<T> => {
    const { headers = {}, ...rest } = config;
    let refreshedToken: string | undefined;
    try {
      refreshedToken = await Firebase.instance.loggedUser?.getIdToken(!0);
    } catch (refreshError) {
      Logger.instance.error("use-auth: firebase token refresh failed", refreshError);
    }

    if (!refreshedToken) {
      Logger.instance.warn("use-auth: unable to refresh firebase token, logging out");
      newWarningGlobalNotification(
        {
          title: "generic.warning",
          description: "generic.sessionExpired",
        },
        { timeout: 7500 }
      );
      await logout();
      router.push(`/${router.locale}${AppRoutes.LOGIN}`);

      return Promise.reject(new ErrorResponse());
    }

    return _doCall(url, {
      ...rest,
      addAuth: !1, // Force manual auth header
      headers: {
        ...headers,
        ...getFirebaseTokenAuthHeader(refreshedToken),
      },
    });
  };

  const _orchestrateLoginMethod = async (idToken: string): Promise<LoginResponse | void> => {
    let innerProviderId = providerId;
    if (innerProviderId === "-1") {
      innerProviderId = await _retrieveOpenIdProviderId();
      setProviderId(innerProviderId);
    }
    const loginPayload = Eva.buildSSOLoginPayload(idToken, innerProviderId);
    try {
      // Logger.instance.debug("use-auth: orchestrated login cart", cart);
      const loginResp = await evaServiceLogin(loginPayload);

      if (loginResp?.Authentication === 2 && loginResp.User) {
        const token = loginResp.User.AuthenticationToken;
        _signalRCleanUp();
        _setEvaToken(token);
      }
    } catch (e) {
      Logger.instance.error("use-auth: orchestrated login failure", e);
      let response: { FailureReason?: number } | void = void 0;
      try {
        response = await (e as EvaHookHttpError).response.json();
      } catch (e) {}
      Logger.instance.warn("Handling EVA error", {
        response,
      });
      setAuthError(
        ErrorResponse.create({
          messages: KikErrorMessages.GENERIC_ERROR,
          errorCode: response?.FailureReason,
        })
      );
      return Promise.reject(e);
    }
  };

  const _retrieveOpenIdProviderId = async (): Promise<string> => {
    const configs = await evaServiceOpenIdConfigs().then((r: any) => r?.Providers || []);
    const config = configs.find((c: any) => c.Name === process.env.NEXT_PUBLIC_OPEN_ID_PROVIDER_NAME);

    return config?.ID || "-1";
  };

  // 🔐 Helper to persist EVA token
  const _setEvaToken = useCallback(
    (token: string | void) => {
      setCurrentUserToken(token);
      if (token) {
        guestCache.remove(CacheKeys.ANONYMOUS_APP_TOKEN);
        evaTokenStorage.set(CacheKeys.EVA_TOKEN, token);
      } else {
        localStorageFactory.remove(CacheKeys.EVA_TOKEN);
        sessionStorageFactory.remove(CacheKeys.EVA_TOKEN);
      }
    },
    [evaTokenStorage, guestCache, localStorageFactory, sessionStorageFactory]
  );

  const _signalRCleanUp = () => {
    sessionStorageFactory.remove(CacheKeys.SIGNAL_R_TOKEN);
  };

  const _trackingEvents: Partial<Record<KikMarketingCloudDoubleOptInProcedures | "_default", string>> = {
    [KikMarketingCloudDoubleOptInProcedures.SIGN_UP_CUSTOMER]: "",
  };

  const _verifyIdToken = () => {
    return Firebase.instance
      .verifyIdToken()
      .catch((e) => Logger.instance.warn("use-auth: idToken verification failed", e));
  };

  // PUBLIC

  /**
   * Perform a login on Eva using the existing Firebase token
   */
  const autoLogin = async (forceFirebaseRefresh = false): Promise<ErrorResponse | void> => {
    Logger.instance.debug("use-auth: calling autoLogin");
    setIsLoading(!0);
    setAuthError(null);
    setSocialProvider(null);
    try {
      // Extract the id_token from the returned authentication object
      const firebaseIdToken = await Firebase.instance.loggedUser?.getIdToken(forceFirebaseRefresh);
      setFirebaseToken(firebaseIdToken); // Need to force this otherwise the asynchronicity breaks with redirects
      await _orchestrateLoginMethod(firebaseIdToken!);
    } catch (err: any) {
      Logger.instance.error("use-auth: login errored", err);
      setAuthError(ApiUtils.errorFactory(err));
      return Promise.reject();
    } finally {
      setIsLoading(!1);
    }
  };

  const createGuestCustomer = async (order: OrdersDtoOrderDto) => {
    invariant(!!order.BillingAddress, `Order is being used to create customer but no address is defined.`);

    let user: Pick<UsersLoggedInUserDto, "DateOfBirth" | "FirstName" | "LastName" | "EmailAddress"> & {
      BillingAddress: AddressesAddressDto;
      ShippingAddress?: AddressesAddressDto;
    } = {
      DateOfBirth: KikUserZodSchemas.guestBirthdayDto.toISODate()!,
      FirstName: order.BillingAddress.FirstName,
      LastName: order.BillingAddress.LastName,
      EmailAddress: order.BillingAddress.EmailAddress,
      BillingAddress: order.BillingAddress,
    };

    if (order.ShippingAddress) {
      user.ShippingAddress = order.ShippingAddress;
    }

    const customer = await evaServiceCreateCustomer({
      OrderID: order.ID,
      AutoLogin: true,
      AccountType: DataModelsUserAccountType.Incognito,
      AttachToOrder: true,
      User: user,
    });

    if (customer?.User?.AuthenticationToken) {
      _signalRCleanUp();
      _setEvaToken(customer.User.AuthenticationToken);
    }

    return customer;
  };

  const emailUpdateCompletion = (data: KikEmailUpdateWithActionCodeFields) => {
    return _doCall(ApiConstants.endpointsMapiV1.authCustomerEmailUpdate, {
      addAuth: !0,
      method: KikRestVerbs.PATCH,
      body: {
        data,
      },
    }).then(() => logout());
  };

  const getFirebaseTokenAuthHeader = (t: string | void = firebaseToken): { Authorization: string } | {} => {
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const getLoyaltyTiers = () => {
    return _doCall<
      KikGenericResponse<
        {
          id: string;
          backendId?: string;
          threshold: number;
          description: string;
          editorialDescription: string;
          image?: string;
        }[]
      >
    >(ApiConstants.endpointsMapiV1.authCustomerLoyaltyTiers, {}).then((r) => {
      return r.data || [];
    });
  };

  const getUserAvailableSubscriptions = () => {
    return _doCall<
      KikGenericResponse<{
        user: UsersSubscriptionsUserSubscriptionDto[];
        loy: UsersSubscriptionsUserSubscriptionDto[];
      }>
    >(ApiConstants.endpointsMapiV1.authCustomerAvailableSubscriptions, {}).then((r) => {
      return {
        user: (r.data!.user || []).filter((s) => !s.Subscription?.Hidden).map((s) => s.Subscription!) || [],
        loy: r.data!.loy || [],
      };
    });
  };

  const getUserSubscriptions = () => {
    return _doCall<
      KikGenericResponse<{
        user: UsersSubscriptionsUserSubscriptionDto[];
        loy: UsersSubscriptionsUserSubscriptionDto[];
      }>
    >(ApiConstants.endpointsMapiV1.authCustomerSubscriptions, {
      addAuth: !0,
      headers: {
        [ApiConstants.headers.X_EVA_TOKEN]: currentUserToken || "",
      },
    }).then((r) => {
      return {
        user: r.data!.user || [],
        loy: r.data!.loy || [],
      };
    });
  };

  const getUserLoyaltyRewards = (orderId?: string, onlyAvailable?: boolean) => {
    return _doCall<KikGenericResponse<LoyaltyReward[]>>(ApiConstants.endpointsMapiV1.ecoLoyaltyPrefigureDiscounts, {
      addAuth: !0,
      queryParams: {
        ...(orderId ? { orderId } : {}),
        // The MAPI drops the rewards with no available coupon.
        ...(onlyAvailable ? { isAvailable: !0 } : {}),
      },
    }).then((r) => r.data ?? []);
  };

  const guestTrackOrderAuthWithActionCode = async (
    params: Omit<GuestTrackOrderAuthParams, "procedure">
  ): Promise<void> => {
    Logger.instance.info(`use-auth: guestTrackOrderAuthWithActionCode requested with email ${params.email}`, {
      orderId: params.orderId,
    });

    return _doCall<KikGenericResponse<{ idToken: string; expiresIn: number }>>(
      ApiConstants.endpointsMapiV1.ecoGuestTrackOrderAuth,
      {
        body: {
          ...params,
          procedure: KikMarketingCloudDoubleOptInProcedures.TRACK_YOUR_ORDER,
        },
      }
    )
      .then((response) => {
        const idToken = response.data?.idToken;
        setGuestTrackingToken(idToken);
      })
      .catch((error) => {
        Logger.instance.warn(
          "use-auth: guestTrackOrderAuthWithActionCode failed, clearing up any guest token that might exist",
          error
        );
        setGuestTrackingToken(void 0);

        return Promise.reject();
      });
  };

  const handleTokenExpired = useCallback(async (): Promise<string | undefined> => {
    // If a refresh is already in progress, wait for it instead of starting a new one
    if (tokenRefreshInProgress.current) {
      return tokenRefreshInProgress.current;
    }

    // If I don't have a firebase token, it means I'm either guest or have a dirty EVA token.
    if (!firebaseToken) {
      Logger.instance.warn("use-auth: token expired (GUEST), clearing up EVA token");
      // We clear the EVA token
      _setEvaToken();
      return;
    }

    Logger.instance.warn("use-auth: token expired (AUTHENTICATED), attempting refresh for authenticated user");

    // Create and store the refresh promise
    const refreshPromise = (async () => {
      try {
        Logger.instance.info("use-auth: Attempting autoLogin to refresh token");
        await autoLogin(!0);

        return currentUserToken || void 0;
      } catch (error) {
        Logger.instance.error("use-auth: autoLogin failed during token refresh", error);
        return void 0;
      } finally {
        // Clear the lock when done
        tokenRefreshInProgress.current = null;
      }
    })();

    tokenRefreshInProgress.current = refreshPromise;

    return refreshPromise;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseToken, evaTokenStorage]);

  const joinKikoMeCompletion = (params: KikUnsubscribeNewsletterParams): Promise<void> => {
    try {
      kikUnsubscribeNewsletterParamsSchema.parse(params);
    } catch (e) {
      setAuthError(
        KikErrorResponse.create({
          messages: KikErrorMessages.BAD_REQUEST,
          errorCode: KikErrorCodes.BAD_REQUEST,
        })
      );

      return Promise.resolve();
    }
    return _doCall(ApiConstants.endpointsMapiV1.authCustomerLoyalty, {
      method: KikRestVerbs.POST,
      queryParams: params,
    });
  };

  const login = async (loginFields: KikCustomerLoginFields): Promise<ErrorResponse | void> => {
    Logger.instance.info(`use-auth: login requested with email ${loginFields.EmailAddress}`);
    setIsLoading(!0);
    setAuthError(null);
    try {
      const rememberMeActive = !!loginFields.RememberMe;
      localStorageFactory.set(CacheKeys.REMEMBERME_DISABLED, !rememberMeActive);
      setIsRememberMeDisabled(!rememberMeActive);
      // setPersistence(true) => localStorage
      // setPersistence(false) => sessionStorage
      await Firebase.instance.setPersistence(rememberMeActive);
      const user = await Firebase.instance.signInWithEmailAndPassword(loginFields.EmailAddress, loginFields.password);

      // Extract the id_token from the returned authentication object
      const firebaseIdToken = await user?.getIdToken();
      setFirebaseToken(firebaseIdToken); // Need to force this otherwise the asynchronicity breaks with redirects
      await _orchestrateLoginMethod(firebaseIdToken!);
    } catch (err: any) {
      Logger.instance.error(`use-auth: login errored for email ${loginFields.EmailAddress}`, err);
      setAuthError(ApiUtils.errorFactory(err));
    } finally {
      setIsLoading(!1);
    }
  };

  const loginAsCustomer = async (
    loginFields: Omit<KikCustomerLoginFields, "RememberMe">
  ): Promise<ErrorResponse | void> => {
    try {
      Logger.instance.warn(`use-auth: loginAsCustomer requested with email ${loginFields.EmailAddress}`);
      await logout().catch((e) => Logger.instance.warn("use-auth: couldn't logout", e));
      const customToken = await _doCall<
        KikGenericResponseBase<{
          token: string;
        }>
      >(ApiConstants.endpointsMapiV1.authImpersonate, {
        body: loginFields,
      }).then((r) => r.data?.token!);
      const token = await Firebase.instance
        .signInWithCustomToken(customToken)
        .then((credential) => credential?.user.getIdToken());
      _orchestrateLoginMethod(token);
    } catch (err: any) {
      Logger.instance.error("use-auth: login errored", err);
    }
  };

  const logout = async (): Promise<ErrorResponse | void> => {
    setIsLoading(!0);
    setAuthError(null);
    try {
      // Sign out from Google
      await Firebase.instance.signOut().catch((e) => Logger.instance.warn("use-auth: Firebase logout failed", e));
      // Sign out from EVA
      await evaServiceLogout().catch((e) => Logger.instance.warn("use-auth: EVA logout failed", e));
      _signalRCleanUp();
      _setEvaToken();
    } catch (err: any) {
      Logger.instance.error("use-auth: logout errored", err);
      setAuthError(new ErrorResponse());
    } finally {
      setIsLoading(!1);
    }
  };

  const onSignalRLogin = function (response: LoginResponse) {
    if (response?.Authentication === 2 && response.User) {
      const token = response.User.AuthenticationToken || "";
      signalRToSessionStorage(token);
      _setEvaToken(token);
      return !0;
    }

    return !1;
  };

  const passwordChange = (params: KikCustomerPasswordUpdateFields) => {
    setIsLoading(!0);
    setAuthError(null);

    return Firebase.instance
      .updatePassword(params.oldPassword, params.password)
      .finally(() => setIsLoading(!1))
      .then(() => logout())
      .catch((err) => {
        Logger.instance.error("use-auth: updatePassword errored", err);
        setAuthError(ApiUtils.errorFactory(err));
        // We rethrow to avoid having if-else outside
        return Promise.reject();
      });
  };

  const passwordResetWithActionCode = (params: KikPasswordResetWithActionCodeFields) => {
    setIsLoading(!0);
    setAuthError(null);

    return _doCall(ApiConstants.endpointsMapiV1.authCustomerPasswordReset, {
      body: params,
    });
  };

  const sendEmailVerification = (request: SendEmailVerificationRequest, origin?: LoginSignUpOrigins): Promise<void> => {
    Logger.instance.info(`use-auth: send email verification ${request.procedure || "NO_PROCEDURE"}`, request);

    return _doCall(ApiConstants.endpointsMapiV1.authSendEmailVerification, {
      body: {
        data: request,
      },
      queryParams: {
        ...(origin ? { origin } : {}),
      },
    })
      .then((r) => {
        // We only track sign-up (customer or prospect) request, since this method is used for all double opt-ins
        const event = _trackingEvents[request.procedure || "_default"];
        event && TrackingUtils.track(TrackingUtils.mapUserEvent(event, { method: "email" }));
        return r;
      })
      .catch((error) => {
        Logger.instance.error("use-auth: sendEmailVerification errored", {
          request,
          error,
        });
        return Promise.reject(error);
      });
  };

  const signalRToSessionStorage = (token: string): void => {
    return sessionStorageFactory.set(CacheKeys.SIGNAL_R_TOKEN, token);
  };

  const signInWithSocialProvider = async (socialKey: KikSocialSignInKeys): Promise<void> => {
    setIsLoading(!0);
    setAuthError(null);
    setSocialProvider(null);
    Logger.instance.info("use-auth: social login requested");
    try {
      await Firebase.instance.signInWithSocialProvider(socialKey);
      // Extract the id_token from the returned authentication object
    } catch (err: any) {
      Logger.instance.warn("use-auth: signInWithSocialProvider errored", err);
      setIsLoading(() => !1);
      // Firebase errors need to be put into authError
      setAuthError(ApiUtils.errorFactory(err));

      return Promise.reject();
    }

    _checkSocialUser(socialKey);
  };

  const signUpCustomerWithActionCode = (
    signUpCustomerWithActionCodeFields: KikCustomerSignUpWithActionCodeFields,
    origin?: LoginSignUpOrigins
  ) => {
    Logger.instance.info(
      `use-auth: signUpCustomerWithActionCode requested with email ${signUpCustomerWithActionCodeFields.EmailAddress}`
    );
    return _doCall<KikGenericResponse<void>>(ApiConstants.endpointsMapiV1.authCustomer, {
      body: signUpCustomerWithActionCodeFields,
      queryParams: {
        ...(origin ? { origin } : {}),
      },
    }).then((r) => {
      TrackingUtils.track(TrackingUtils.mapUserEvent("sign_up", { method: "email" }));
      const firstWarning = r.messages?.find((m) => m.type === KikResponseMessageTypes.WARNING);
      if (firstWarning) {
        newWarningGlobalNotification(
          {
            title: "generic.signUpWarning",
            description: `ERROR.${firstWarning.message}`, // PUSH_SUBSCRIPTIONS_ERROR
          },
          { timeout: 7500 }
        );
      }
      return r;
    });
  };

  const signUpProspectWithActionCode = (prospectSignUpFields: KikProspectSignUpWithActionCodeFields): Promise<void> => {
    return _doCall(ApiConstants.endpointsMapiV1.authProspect, {
      body: prospectSignUpFields,
    })
      .then(() => {
        TrackingUtils.track(
          TrackingUtils.mapUserEvent("generate_lead", { lead_type: "newsletter", lead_result: "submit_ok" })
        );
        return Promise.resolve();
      })
      .catch(() => {
        TrackingUtils.track(
          TrackingUtils.mapUserEvent("generate_lead", { lead_type: "newsletter", lead_result: "submit_ko" })
        );
        return Promise.reject();
      });
  };

  /**
   * Request a new social registration
   */
  const signUpSocial = async (
    socialCustomer: KikSocialCustomerFields,
    socialKey: KikSocialSignInKeys
  ): Promise<void> => {
    return _doCall(ApiConstants.endpointsMapiV1.authCustomerSocial, {
      body: socialCustomer,
    }).then((r) => {
      TrackingUtils.track(TrackingUtils.mapUserEvent("sign_up_requested", { method: socialKey }));
      return r;
    });
  };

  const unsubscribeNewsletterCompletion = (
    unsubscribeNewsletterParams: KikUnsubscribeNewsletterParams
  ): Promise<void> => {
    try {
      kikUnsubscribeNewsletterParamsSchema.parse(unsubscribeNewsletterParams);
    } catch (e) {
      setAuthError(
        KikErrorResponse.create({
          messages: KikErrorMessages.BAD_REQUEST,
          errorCode: KikErrorCodes.BAD_REQUEST,
        })
      );

      return Promise.resolve();
    }
    return _doCall(ApiConstants.endpointsMapiV1.authProspect, {
      method: KikRestVerbs.DELETE,
      queryParams: unsubscribeNewsletterParams,
    });
  };

  const updateUserProfile = (firstName: string, lastName: string) => {
    setIsLoading(!0);
    setAuthError(null);

    return Firebase.instance
      .updateUserProfile({
        displayName: `${firstName} ${lastName}`,
      })
      .catch((err) => {
        Logger.instance.error("use-auth: updateUserProfile errored", err);
        setAuthError(ApiUtils.errorFactory(err));
        // We rethrow to avoid having if-else outside
        return Promise.reject();
      })
      .finally(() => setIsLoading(!1));
  };

  // END PUBLIC METHODS

  const hasEvaAuth = !!currentUserToken && !!user?.ID; // Do not consider anonymous users as authenticated

  useEffect(() => {
    let tokenEvent = 0;
    const unsubscribe = Firebase.instance.addIdTokenChangedCallback(async (firebaseUser) => {
      const currentEvent = ++tokenEvent;
      // Session cleanup and the stored-user fallback remain in the auth-state listener.
      if (!firebaseUser) return;

      try {
        const result = await firebaseUser.getIdTokenResult();
        if (currentEvent !== tokenEvent) return;
        setFirebaseToken(result.token);
        setFirebaseIdTokenResult(result);
      } catch (error) {
        Logger.instance.warn("use-auth: Firebase token synchronization failed", error);
      }
    });

    return () => {
      ++tokenEvent; // Ignore pending reads after unmount.
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (Firebase.instance.socialLoginUXMode && !isLoading) {
      Logger.instance.warn("use-auth: useEffect checkRedirectResult", Firebase.instance.socialLoginUXMode);
      Firebase.instance.socialLoginUXMode !== KikSocialSignInUxModes.POPUP && _checkRedirectResult();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Firebase.instance.socialLoginUXMode]);

  useEffect(() => {
    unsubscribeAuthObs.current?.(); // Turn off previous listeners
    unsubscribeAuthObs.current = Firebase.instance.addAuthStateChangedCallback(async (firebaseUser) => {
      let newToken: string | void = void 0;
      let newIdTokenResult: IdTokenResult | void = void 0;
      // A new user session is started
      if (firebaseUser) {
        newToken = await firebaseUser.getIdToken();
        newIdTokenResult = await _verifyIdToken();
        if (user) {
          if (user.AccountType !== DataModelsUserAccountType.Standard) {
            Logger.instance.warn(
              `use-auth: Firebase token with non standard user (${user.EmailAddress})! Performing Firebase logout!`
            );
            await Firebase.instance.signOut(); // Logout from Firebase only
          } else if (user.EmailAddress !== firebaseUser.email) {
            // [TS]: starting November 2025, we started receiving emails from EVA with a capital letter. We fix those emails here.
            const evaEmailLower = user.EmailAddress!.toLowerCase();
            const firebaseEmailLower = firebaseUser.email?.toLowerCase();

            if (evaEmailLower === firebaseEmailLower) {
              if (sessionStorageFactory.get(CacheKeys.EMAIL_NORMALISED) !== firebaseEmailLower) {
                Logger.instance.info("use-auth: email address casing mismatch, updating user email address in EVA");
                sessionStorageFactory.set(CacheKeys.EMAIL_NORMALISED, firebaseEmailLower);
                await updateUser({
                  ID: user.ID,
                  EmailAddress: evaEmailLower,
                });
              } else {
                Logger.instance.debug(
                  "use-auth: email already normalized for this session, skipping duplicate updateUser call"
                );
              }
            } else {
              Logger.instance.warn("use-auth: Firebase/EVA user mismatch! Performing logout!", {
                userEmail: user.EmailAddress,
                firebaseEmail: firebaseUser.email,
              });
              logout();
            }
          }
        }
      } else {
        // Clear up the check for normalised email
        sessionStorageFactory.remove(CacheKeys.EMAIL_NORMALISED);
        const signalR = sessionStorageFactory.get(CacheKeys.SIGNAL_R_TOKEN);
        if (user) {
          // So basically if user is customer, but we don't have a signalR, or there is a signalR, but it doesn't match the user token, then cleanup
          if (
            (!signalR && user.AccountType !== DataModelsUserAccountType.Incognito) ||
            (signalR && signalR !== currentUserToken)
          ) {
            Logger.instance.warn(
              "use-auth: found customer EVA user but no firebase session and no signalR, cleaning up!",
              {
                userEmail: user.EmailAddress,
              }
            );
            _signalRCleanUp(); // void custom SignalR backup
            setCurrentUserToken(""); // void EVA session
          }
        }
      }
      setFirebaseToken(newToken);
      setFirebaseIdTokenResult(newIdTokenResult);
      setIsLoadingUser(!1);
    });

    return () => {
      unsubscribeAuthObs.current?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserToken, user]);

  useEffect(() => {
    if (socialProvider && !isLoading) {
      Logger.instance.info(`SocialSignIn: autologin can be performed for ${socialProvider}!`);
      autoLogin().then(() => {
        Logger.instance.debug(`SocialSignIn: autologin complete with ${socialProvider}!`);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socialProvider, isLoading]);

  useEffect(() => {
    const evaId = user?.ID?.toString();
    const backendId = user?.BackendID;
    const sessionAttrs = {
      // cart: cart?.ID,
      id: evaId,
      backendId: evaId === backendId ? void 0 : backendId, // Identify legacy users
      email: user?.EmailAddress,
      name: `${user?.FirstName} ${user?.LastName}`,
      userAccountType: ["STANDARD", "GUEST"][user?.AccountType || 3],
    };
    /**
     * Since instance already exists, this won't do much besides modifying the config
     * updating (overwriting) the tags every time the user id changes.
     * This will also make tags available everywhere a log (default instance) is fired
     */
    Logger.init({
      customTags: sessionAttrs,
    });
    datadogRum.setUser(sessionAttrs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // cart?.ID,
    user?.ID,
  ]);

  return {
    // Values
    authError,
    currentUserToken,
    enabledSocials: Firebase.instance.enabledSocials,
    firebaseToken,
    firebaseIdTokenResult,
    firebaseUser: Firebase.instance.loggedUser,
    guestTrackingToken,
    hasEvaAuth,
    isAuthenticated: !!firebaseToken && hasEvaAuth && user.AccountType === DataModelsUserAccountType.Standard,
    isLoading,
    isLoadingUser,
    isMigrated: new RegExp(process.env.NEXT_PUBLIC_CID_PATTERN!).test(user?.BackendID || ""),
    socialProvider,
    user,
    // Methods
    autoLogin,
    createGuestCustomer,
    emailUpdateCompletion,
    getLoyaltyTiers,
    getUserAvailableSubscriptions,
    getUserLoyaltyRewards,
    getUserSubscriptions,
    handleTokenExpired,
    joinKikoMeCompletion,
    login,
    loginAsCustomer,
    logout,
    onSignalRLogin,
    passwordChange,
    passwordResetWithActionCode,
    refreshUserData,
    guestTrackOrderAuthWithActionCode,
    sendEmailVerification,
    setGuestTrackingToken,
    signalRToSessionStorage,
    signInWithSocialProvider,
    signUpCustomerWithActionCode,
    signUpProspectWithActionCode,
    signUpSocial,
    unsubscribeNewsletterCompletion,
    updateUserProfile,
  };
};

export default useAuth;
